/**
 * Canonical activity URLs.
 *
 * Activity detail pages are served at /<category-slug>/<activity-slug>, so the
 * path itself says what the page shows. The category segment is the activity's
 * most specific category: a subcategory when it has one, otherwise the parent
 * category it hangs under.
 *
 * The segment is a readable hint, not a lookup key: the page loads by activity
 * slug and swaps the URL for the canonical path when the segment doesn't match.
 * So a landingpage slug that also exists as a category slug keeps working at
 * /<slug> (one segment), while /<slug>/<activity-slug> is the activity.
 *
 * An activity without a category has nothing to name in that first segment, so
 * it keeps the same two-segment shape with the neutral /ausflug/<slug> — one
 * URL form for every activity, no legacy variant to redirect.
 */

const NEUTRAL_SEGMENT = 'ausflug';

/**
 * The activity's editorial main category, or null when none is set or the id no
 * longer resolves. It decides the breadcrumb and the category segment of the
 * canonical URL, so the site never has to guess between several valid categories.
 */
export function primaryCategory(activity, categories = []) {
  const id = activity?.primary_category_id;
  if (!id) return null;
  return categories.find(c => c.id === id) || null;
}

/** The activity's assigned categories, resolved and in stored order. */
function assignedCategories(activity, categories) {
  return (activity?.category_ids || [])
    .map(id => categories.find(c => c.id === id))
    .filter(Boolean);
}

/**
 * The category that decides the breadcrumb and the canonical URL. The editorial
 * primary wins when it is set; otherwise the activity's most specific category:
 * as soon as a subcategory is assigned, that subcategory is it, never the parent
 * it hangs under. Only an activity without a subcategory falls back to its main
 * category. Ties break on sort_order, then slug, so the result is the same on
 * every render.
 */
export function effectivePrimaryCategory(activity, categories = []) {
  const manual = primaryCategory(activity, categories);
  if (manual) return manual;

  const assigned = assignedCategories(activity, categories);

  // Most specific category wins.
  const subcategory = assigned
    .filter(c => c.parent_category_id)
    .sort((a, b) =>
      (a.sort_order || 0) - (b.sort_order || 0) ||
      String(a.slug).localeCompare(String(b.slug))
    )[0];

  return subcategory || assigned.find(c => !c.parent_category_id) || null;
}

/**
 * The activity's categories with the one that decides the page first, so a
 * consumer that reads the first entry as "the" category (the breadcrumb) shows
 * the subcategory whenever the activity has one.
 */
export function orderedActivityCategories(activity, categories = []) {
  const list = categories.filter(c => activity?.category_ids?.includes(c.id));
  const primary = effectivePrimaryCategory(activity, categories);
  if (!primary) return list;
  return [primary, ...list.filter(c => c.id !== primary.id)];
}

/** Category slug used for the path, lowercased (slugs are stored inconsistently). */
export function activityCategorySlug(activity, categories = []) {
  const slug = effectivePrimaryCategory(activity, categories)?.slug;
  return slug ? String(slug).toLowerCase() : null;
}

/**
 * Path for an activity: '/<category-slug>/<activity-slug>', '/ausflug/<slug>'
 * when no category is known, or null when the activity has no slug yet.
 */
export function activityPath(activity, categories = []) {
  if (!activity?.slug) return null;

  const categorySlug = activityCategorySlug(activity, categories);
  return categorySlug
    ? `/${categorySlug}/${activity.slug}`
    : `/${NEUTRAL_SEGMENT}/${activity.slug}`;
}

/** Two-segment routes that are not activity detail pages. */
const OTHER_DETAIL_SEGMENTS = ['magazin', 'artikel', 'updates'];

/** True for a path shaped like an activity detail page: '/<category>/<activity>'. */
export function isActivityDetailPath(pathname) {
  const segments = String(pathname || '').split('?')[0].split('/').filter(Boolean);
  return segments.length === 2 && !OTHER_DETAIL_SEGMENTS.includes(segments[0].toLowerCase());
}